// Ensure theme strings are available for variant availability states
window.theme = window.theme || {};
window.theme.strings = window.theme.strings || {};
window.theme.strings.soldOut = window.theme.strings.soldOut || 'Sold Out';
window.theme.strings.addToCart = window.theme.strings.addToCart || 'Add to Cart'; 
window.theme.strings.unavailable = window.theme.strings.unavailable || 'Unavailable';

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        
        // Clean up any observer
        if (this.labelObserver) {
          this.labelObserver.disconnect();
          this.labelObserver = null;
        }
        
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Set up MutationObserver to hide any 'variant sold out or unavailable' messages
            this.setupLabelUnavailableObserver();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      setupLabelUnavailableObserver() {
        // Immediately hide any existing 'label-unavailable' elements
        const labels = this.modalContent.querySelectorAll('.label-unavailable:not(.visually-hidden)');
        labels.forEach(label => {
          label.classList.add('visually-hidden');
        });

        // Set up observer to watch for new 'label-unavailable' elements
        const observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.type === 'childList' && mutation.addedNodes.length) {
              mutation.addedNodes.forEach(node => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Check the added node itself
                  if (node.classList && node.classList.contains('label-unavailable') && 
                      !node.classList.contains('visually-hidden')) {
                    node.classList.add('visually-hidden');
                  }
                  
                  // Also check any children
                  const labelElements = node.querySelectorAll('.label-unavailable:not(.visually-hidden)');
                  labelElements.forEach(label => {
                    label.classList.add('visually-hidden');
                  });
                }
              });
            }
          });
        });

        // Start observing
        observer.observe(this.modalContent, { 
          childList: true, 
          subtree: true 
        });
        
        // Store the observer for cleanup later
        this.labelObserver = observer;
      }
    }
  );
}

function quickAdd(variantId) {
  const data = {
    items: [{
      id: variantId,
      quantity: 1
    }]
  };

  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/javascript'
    },
    body: JSON.stringify(data)
  })
  .then(response => response.json())
  .then(data => {
    // Update cart and open drawer
    const button = event.target;
    const originalText = button.textContent;
    button.textContent = window.theme.strings.addedToCart || 'Added!';
    
    // Use the same function for consistent behavior
    updateCartAndOpenDrawer(button);
    
    setTimeout(() => {
      button.textContent = originalText;
    }, 2000);
  })
  .catch((error) => {
    console.error('Error:', error);
    // Show error message
    const button = event.target;
    button.textContent = window.theme.strings.addError || 'Error';
    
    setTimeout(() => {
      button.textContent = window.theme.strings.addToCart || 'Add to Cart';
    }, 2000);
  });
}

// Add HTMLUpdateUtility if it doesn't exist already
if (typeof HTMLUpdateUtility === 'undefined') {
  window.HTMLUpdateUtility = {
    setInnerHTML(element, html) {
      element.innerHTML = html;
      
      // Execute scripts if needed
      element.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  };
}

// Ensure that custom elements are ready when needed
function ensureCustomElementDefined(elementName, callback) {
  if (customElements.get(elementName)) {
    callback();
  } else {
    customElements.whenDefined(elementName).then(callback);
  }
}

// Helper function for showing a quick-add modal
function showQuickAddModal(modalElement, opener) {
  ensureCustomElementDefined('quick-add-modal', () => {
    // Check if we're dealing with a proper custom element instance
    if (typeof modalElement.show === 'function') {
      modalElement.show(opener);
    } else {
      // Basic fallback for handling non-initialized components
      const modalContent = modalElement.querySelector('.quick-add-modal__content');
      if (modalContent) {
        modalContent.setAttribute('open', '');
        modalContent.style.display = 'block';
        
        // If we know the product URL, fetch the content
        const productUrl = opener.getAttribute('data-product-url');
        if (productUrl) {
          // Try to extract product ID from modal ID
          let productId = modalElement.id;
          if (productId) {
            productId = productId.replace('QuickAdd-', '');
            fetchProductContent(productUrl, productId, opener);
          }
        }
      }
    }
  });
}

// Direct add to cart for product cards
document.addEventListener('DOMContentLoaded', function() {
  // Initialize card add-to-cart buttons
  initCardAddToCartButtons();
  
  // Handle dynamic content loading
  document.addEventListener('shopify:section:load', initCardAddToCartButtons);
});

function initCardAddToCartButtons() {
  // Find all card__add-to-cart-btn buttons
  const addToCartButtons = document.querySelectorAll('.card__add-to-cart-btn');
  
  // For each button, apply necessary styles and event handling
  addToCartButtons.forEach(button => {
    // Store original text
    if (!button.hasAttribute('data-original-text')) {
      button.setAttribute('data-original-text', button.textContent);
    }
    
    // Apply high z-index and relative positioning for proper stacking
    button.style.position = 'relative';
    button.style.zIndex = '9999';
    
    // Make the button container also have high z-index
    const buttonContainer = button.closest('.card__add-to-cart');
    if (buttonContainer) {
      buttonContainer.style.position = 'relative';
      buttonContainer.style.zIndex = '9999';
    }
  });
  
  // Using event delegation to handle card add-to-cart clicks
  document.body.removeEventListener('click', handleCardAddToCartClick);
  document.body.addEventListener('click', handleCardAddToCartClick);
  
  // Prevent card link propagation on button interactions
  document.body.removeEventListener('mousedown', preventCardLinkPropagation);
  document.body.addEventListener('mousedown', preventCardLinkPropagation, true);
  
  // Disable card links when hovering over add-to-cart buttons
  document.body.removeEventListener('mouseover', handleButtonHover);
  document.body.addEventListener('mouseover', handleButtonHover);
  document.body.removeEventListener('mouseout', handleButtonMouseOut);
  document.body.addEventListener('mouseout', handleButtonMouseOut);
}

function handleCardAddToCartClick(e) {
  const button = e.target.closest('.card__add-to-cart-btn');
  if (!button) return;

  // If this button is inside a modal-opener, let the modal-opener handle the event
  if (button.closest('modal-opener')) {
    // Do NOT preventDefault or stopPropagation; let the custom element handle it
    return;
  }

  // Stop event propagation to prevent card link clicking
  e.stopPropagation();
  e.preventDefault();

  // Get variant ID and product URL
  const variantId = button.getAttribute('data-variant-id');
  const productUrl = button.getAttribute('data-product-url');

  // Get the card product element to check for quick-add/quick-shop functionality
  const card = button.closest('.card-product') || button.closest('.card');
  if (!card) return;

  // Check if product has multiple variants by looking at the card
  const noVariantsButton = card.querySelector('.quick-add__submit:not([aria-haspopup="dialog"])');
  const singleVariantForm = card.querySelector('product-form');
  const hasDirectAddButton = noVariantsButton || singleVariantForm;

  // If the product has only one variant, add directly to cart
  if (hasDirectAddButton && variantId) {
    // Direct add to cart for single variant products
    addToCart(variantId, button);
    return;
  }

  // Find any quick-add opener buttons in the card (standard or bulk)
  const standardQuickAdd = card.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
  const bulkQuickAdd = card.querySelector('modal-opener[id^="QuickBulk-"]');
  const anyQuickAddButton = card.querySelector('.quick-add__submit[aria-haspopup="dialog"]');

  // If we have a quick-add opener for variants, use it
  if (standardQuickAdd) {
    // Let the modal-opener handle the event (already handled above)
    return;
  }
  // Check for bulk quick-add
  else if (bulkQuickAdd) {
    // Let the modal-opener handle the event (already handled above)
    return;
  }
  // As a fallback, try to find any quick-add button
  else if (anyQuickAddButton) {
    anyQuickAddButton.click();
    return;
  }
  // If we have no modal but have a URL, use it to fetch and show the quickshop modal manually
  else if (productUrl && (card.querySelector('.quick-add') || card.closest('.card-wrapper'))) {
    // See if we can find a product ID from the card
    const productId = extractProductId(card);

    if (productId) {
      // Try to find an existing quick-add-modal for this product first
      let quickAddModal = document.querySelector(`quick-add-modal#QuickAdd-${productId}`);
      if (!quickAddModal) {
        // Look for a modal-dialog as alternative
        const modalDialog = document.querySelector(`modal-dialog[id^="QuickAddBulk-${productId}"]`);
        if (modalDialog) {
          // Use a modal-dialog for bulk quick-add if available
          modalDialog.querySelector('button')?.click();
          return;
        }

        // Create a new modal if we couldn't find one
        quickAddModal = document.createElement('quick-add-modal');
        quickAddModal.id = `QuickAdd-${productId}`;
        quickAddModal.className = 'quick-add-modal';
        quickAddModal.innerHTML = `
          <div role="dialog" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">
            <button type="button" class="quick-add-modal__toggle" aria-label="Close">
              <svg viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/>
              </svg>
            </button>
            <div id="QuickAddInfo-${productId}" class="quick-add-modal__content-info"></div>
          </div>
        `;
        document.body.appendChild(quickAddModal);
      }

      // Show loading state on the button
      button.disabled = true;
      button.classList.add('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }

      // Show the modal with the product content using our helper
      showQuickAddModal(quickAddModal, button);
      return;
    }
  }

  // Fallback: direct add to cart when we couldn't determine variants or find a modal
  if (variantId) {
    addToCart(variantId, button);
  }
}

// Helper function for adding to cart
function addToCart(variantId, button) {
  // Disable button and show loading state
  button.disabled = true;
  button.classList.add('loading');
  const spinner = button.querySelector('.loading__spinner');
  if (spinner) {
    spinner.classList.remove('hidden');
  }

  // Add to cart via fetch
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/javascript'
    },
    body: JSON.stringify({
      items: [{
        id: variantId,
        quantity: 1
      }]
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  })
  .then(data => {
    // Show success feedback
    button.textContent = window.theme.strings.addedToCart || 'Added!';

    // Update cart count and open drawer
    updateCartAndOpenDrawer(button);

    // Reset button state after delay
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove('loading');
      button.textContent = button.getAttribute('data-original-text') || window.theme.strings.addToCart || 'Add to Cart';
    }, 1000);
  })
  .catch(error => {
    console.error('Error adding to cart:', error);
    
    // Show error state
    button.textContent = window.theme.strings.addError || 'Error';
    
    // Reset button state
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove('loading');
      button.textContent = button.getAttribute('data-original-text') || window.theme.strings.addToCart || 'Add to Cart';
    }, 2000);
  });
}

// Helper function to extract product ID from card
function extractProductId(card) {
  // Try multiple methods to find the product ID
  
  // 1. Try from modal-opener data-modal attribute
  const modalOpener = card.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
  if (modalOpener) {
    const modalId = modalOpener.getAttribute('data-modal');
    if (modalId) {
      const match = modalId.match(/#QuickAdd-(\d+)/);
      if (match && match[1]) return match[1];
    }
  }
  
  // 2. Try from bulk modal opener
  const bulkOpener = card.querySelector('modal-opener[id^="QuickBulk-"]');
  if (bulkOpener) {
    const openerId = bulkOpener.id;
    if (openerId) {
      const match = openerId.match(/QuickBulk-(\d+)/);
      if (match && match[1]) return match[1];
    }
  }
  
  // 3. Try from button data-variant-id by checking card for product form
  const productForm = card.querySelector('form[data-type="add-to-cart-form"]');
  if (productForm) {
    const productId = productForm.id.match(/(\d+)$/);
    if (productId && productId[1]) return productId[1];
  }
  
  // 4. Try from card ID 
  const cardId = card.id;
  if (cardId) {
    const match = cardId.match(/(\d+)$/);
    if (match && match[1]) return match[1];
  }
  
  // 5. Try from any ID in the card that might contain product ID
  const elementsWithId = card.querySelectorAll('[id]');
  for (const element of elementsWithId) {
    const match = element.id.match(/[_-](\d+)(?:[_-]|$)/);
    if (match && match[1]) return match[1];
  }
  
  // 6. Try from URL in links
  const productLinks = card.querySelectorAll('a[href*="/products/"]');
  if (productLinks.length > 0) {
    // Extract the product handle and use that
    const href = productLinks[0].getAttribute('href');
    if (href) {
      return href.split('/').pop().split('?')[0]; // This gives us the handle which is also useful
    }
  }
  
  return null;
}

// Helper function to fetch product content for a modal
function fetchProductContent(productUrl, productId, button) {
  fetch(productUrl)
    .then((response) => response.text())
    .then((responseText) => {
      const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const productElement = responseHTML.querySelector('product-info');
      
      if (productElement) {
        const quickAddInfo = document.querySelector(`#QuickAddInfo-${productId}`);
        if (quickAddInfo) {
          // Process the HTML and inject it
          if (window.HTMLUpdateUtility) {
            window.HTMLUpdateUtility.setInnerHTML(quickAddInfo, productElement.outerHTML);
          } else {
            quickAddInfo.innerHTML = productElement.outerHTML;
          }
          
          // Initialize any payment buttons
          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          // Initialize 3D models if needed
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
        }
      }
    })
    .catch(error => {
      console.error('Error loading quick-add content:', error);
    })
    .finally(() => {
      // Reset button state
      if (button) {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      }
    });
}

// New helper function to update the cart and open drawer
function updateCartAndOpenDrawer(trigger) {
  // First try to update the cart
  if (typeof window.refreshCart === 'function') {
    window.refreshCart();
  } else {
    // Fallback: Trigger cart update event
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // Then try to open the cart drawer with various possible methods
  setTimeout(() => {
    // Try the cart-drawer web component
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open(trigger);
        return;
      } else if (typeof cartDrawer.show === 'function') {
        cartDrawer.show(trigger);
        return;
      } else if (cartDrawer.hasAttribute('open') !== undefined) {
        cartDrawer.setAttribute('open', '');
        return;
      }
    }

    // Try cart-notification component
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.open === 'function') {
      cartNotification.open();
      return;
    }

    // Try theme-specific cart drawer
    const themeCartDrawer = document.getElementById('CartDrawer');
    if (themeCartDrawer) {
      themeCartDrawer.classList.add('active', 'is-open');
      document.body.classList.add('cart-drawer-open');
      return;
    }

    // Try fetching cart.js to force a cart update
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('Cart updated:', cart);
        // Dispatch cart update event that theme might listen for
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      })
      .catch(error => console.error('Error updating cart:', error));
  }, 100); // Small delay to ensure cart is updated first
}

function preventCardLinkPropagation(e) {
  // If click is on add-to-cart button or its container, prevent event bubbling
  const button = e.target.closest('.card__add-to-cart-btn');
  const container = e.target.closest('.card__add-to-cart');
  
  if (button || (container && !container.contains(e.target.closest('a[href]')))) {
    e.stopPropagation();
  }
}

function handleButtonHover(e) {
  const button = e.target.closest('.card__add-to-cart-btn');
  if (!button) return;
  
  // Find the card element
  const card = button.closest('.card-product');
  if (!card) return;
  
  // Find all anchor links in the card and temporarily disable them
  const cardLinks = card.querySelectorAll('a[href]');
  cardLinks.forEach(link => {
    if (!link.hasAttribute('data-original-href')) {
      link.setAttribute('data-original-href', link.getAttribute('href'));
      link.setAttribute('data-original-style', link.getAttribute('style') || '');
      link.removeAttribute('href');
      link.style.pointerEvents = 'none';
    }
  });
}

function handleButtonMouseOut(e) {
  const button = e.target.closest('.card__add-to-cart-btn');
  if (!button) return;
  
  // Check if we're still within the button or its container
  const relatedTarget = e.relatedTarget;
  if (button.contains(relatedTarget) || 
      (button.closest('.card__add-to-cart') && 
       button.closest('.card__add-to-cart').contains(relatedTarget))) {
    return;
  }
  
  // Find the card element
  const card = button.closest('.card-product');
  if (!card) return;
  
  // Restore anchor links
  const cardLinks = card.querySelectorAll('a[data-original-href]');
  cardLinks.forEach(link => {
    link.setAttribute('href', link.getAttribute('data-original-href'));
    const originalStyle = link.getAttribute('data-original-style');
    if (originalStyle) {
      link.setAttribute('style', originalStyle);
    } else {
      link.removeAttribute('style');
    }
    link.removeAttribute('data-original-href');
    link.removeAttribute('data-original-style');
  });
}

// Global cart refresh function
window.refreshCart = function() {
  fetch(window.Shopify.routes.root + 'cart.js')
    .then(response => response.json())
    .then(cart => {
      // Update cart count in header
      const cartCountElements = document.querySelectorAll('.cart-count-bubble');
      if (cartCountElements.length > 0) {
        const itemCount = cart.item_count;
        cartCountElements.forEach(element => {
          element.textContent = itemCount;
          element.classList.toggle('hidden', itemCount === 0);
        });
      }

      // Update any cart item count spans
      const cartItemCountElements = document.querySelectorAll('[data-cart-item-count]');
      cartItemCountElements.forEach(element => {
        element.textContent = cart.item_count;
        element.classList.toggle('hidden', cart.item_count === 0);
      });

      // Dispatch cart update event for other components to listen for
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart }
      }));
    })
    .catch(error => console.error('Error fetching cart:', error));
};

// Initialize refreshCart on page load
document.addEventListener('DOMContentLoaded', function() {
  // Initial cart refresh
  if (typeof window.refreshCart === 'function') {
    window.refreshCart();
  }
});

// Quick Add variant handling
document.addEventListener('DOMContentLoaded', function() {
  // Handle variant changes in quick-add modals
  document.addEventListener('change', function(e) {
    // Look for variant selects inside quick-add modals
    const variantSelect = e.target.closest('select');
    if (!variantSelect) return;
    
    const quickAddModal = e.target.closest('quick-add-modal');
    if (!quickAddModal) return;
    
    // Find the form
    const productForm = quickAddModal.querySelector('product-form');
    if (!productForm) return;
    
    // Find the submit button
    const submitButton = productForm.querySelector('[type="submit"]');
    if (!submitButton) return;
    
    const submitButtonText = submitButton.querySelector('span');
    if (!submitButtonText) return;
    
    // Get all the selected options
    const selects = Array.from(quickAddModal.querySelectorAll('select[name^="options["]'));
    const radioInputs = Array.from(quickAddModal.querySelectorAll('input[type="radio"]:checked'));
    
    // Get the selected variant data
    const variantJson = quickAddModal.querySelector('[data-selected-variant]');
    if (!variantJson) return;
    
    // Get the product data
    let productData;
    try {
      const productInfoEl = quickAddModal.querySelector('product-info');
      if (!productInfoEl) return;
      
      const productId = productInfoEl.dataset.productId;
      if (!productId) return;
      
      // Fetch product data to match selected options to a variant
      fetch(`${window.Shopify.routes.root}products/${productId}.js`)
        .then(response => response.json())
        .then(data => {
          productData = data;
          
          // Get the selected option values
          const selectedOptions = [];
          selects.forEach(select => {
            selectedOptions.push(select.value);
          });
          
          radioInputs.forEach(radio => {
            selectedOptions.push(radio.value);
          });
          
          // Find the matching variant
          let matchingVariant = null;
          if (productData && productData.variants) {
            matchingVariant = productData.variants.find(variant => {
              return variant.options.every((option, index) => {
                return selectedOptions.includes(option);
              });
            });
          }
          
          // Update the add to cart button based on variant availability
          if (matchingVariant) {
            // Update form's hidden variant ID input
            const variantInput = productForm.querySelector('[name="id"]');
            if (variantInput) {
              variantInput.value = matchingVariant.id;
              variantInput.disabled = !matchingVariant.available;
            }
            
            // Update the add to cart button text and state
            if (!matchingVariant.available) {
              // Variant is sold out
              submitButton.disabled = true;
              submitButtonText.textContent = window.theme.strings.soldOut || 'Sold Out';
              submitButton.classList.add('sold-out');
            } else {
              // Variant is available
              submitButton.disabled = false;
              submitButtonText.textContent = window.theme.strings.addToCart || 'Add to Cart';
              submitButton.classList.remove('sold-out');
            }
            
            // Hide any unavailable messages on the variant options
            const unavailableLabels = quickAddModal.querySelectorAll('.label-unavailable');
            unavailableLabels.forEach(label => {
              if (!label.classList.contains('visually-hidden')) {
                label.classList.add('visually-hidden');
              }
            });
          }
        })
        .catch(error => {
          console.error('Error fetching product data:', error);
        });
    } catch (e) {
      console.error('Error processing variant change:', e);
    }
  });
  
  // Add event delegation to handle quick-add-modal loading
  document.addEventListener('page:loaded', function() {
    setupQuickAddVariantHandling();
  });
  
  // Setup handling when document is ready
  setupQuickAddVariantHandling();
});

// Setup initial variant handling on page load
function setupQuickAddVariantHandling() {
  // Ensure theme strings are available
  window.theme = window.theme || {};
  window.theme.strings = window.theme.strings || {};
  window.theme.strings.soldOut = window.theme.strings.soldOut || 'Sold Out';
  window.theme.strings.addToCart = window.theme.strings.addToCart || 'Add to Cart';
  
  // Find all quick-add modals
  const quickAddModals = document.querySelectorAll('quick-add-modal');
  
  quickAddModals.forEach(modal => {
    // Set up an observer to detect when content is added to the modal
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          // Check if product-form is added
          const productForm = modal.querySelector('product-form');
          if (productForm) {
            // Wait a bit for the form to initialize
            setTimeout(() => {
              // Get initial variant and update button
              const variantJson = modal.querySelector('[data-selected-variant]');
              if (variantJson) {
                try {
                  const variant = JSON.parse(variantJson.textContent);
                  
                  // Find the submit button
                  const submitButton = productForm.querySelector('[type="submit"]');
                  if (!submitButton) return;
                  
                  const submitButtonText = submitButton.querySelector('span');
                  if (!submitButtonText) return;
                  
                  // Update the add to cart button based on variant availability
                  if (variant) {
                    if (!variant.available) {
                      // Variant is sold out
                      submitButton.disabled = true;
                      submitButtonText.textContent = window.theme.strings.soldOut || 'Sold Out';
                      submitButton.classList.add('sold-out');
                    } else {
                      // Variant is available
                      submitButton.disabled = false;
                      submitButtonText.textContent = window.theme.strings.addToCart || 'Add to Cart';
                      submitButton.classList.remove('sold-out');
                    }
                  }
                  
                  // Hide any unavailable messages on the variant options
                  const unavailableLabels = modal.querySelectorAll('.label-unavailable');
                  unavailableLabels.forEach(label => {
                    if (!label.classList.contains('visually-hidden')) {
                      label.classList.add('visually-hidden');
                    }
                  });
                } catch (e) {
                  console.error('Error parsing selected variant data:', e);
                }
              }
            }, 100);
          }
        }
      });
    });
    
    // Start observing the modal content
    observer.observe(modal, {
      childList: true,
      subtree: true
    });
  });
  
  // Also handle any modals that already have content
  quickAddModals.forEach(modal => {
    const productForm = modal.querySelector('product-form');
    if (productForm) {
      const variantJson = modal.querySelector('[data-selected-variant]');
      if (variantJson) {
        try {
          const variant = JSON.parse(variantJson.textContent);
          
          // Find the submit button
          const submitButton = productForm.querySelector('[type="submit"]');
          if (!submitButton) return;
          
          const submitButtonText = submitButton.querySelector('span');
          if (!submitButtonText) return;
          
          // Update the add to cart button based on variant availability
          if (variant) {
            if (!variant.available) {
              // Variant is sold out
              submitButton.disabled = true;
              submitButtonText.textContent = window.theme.strings.soldOut || 'Sold Out';
              submitButton.classList.add('sold-out');
            } else {
              // Variant is available
              submitButton.disabled = false;
              submitButtonText.textContent = window.theme.strings.addToCart || 'Add to Cart';
              submitButton.classList.remove('sold-out');
            }
          }
          
          // Hide any unavailable messages on the variant options
          const unavailableLabels = modal.querySelectorAll('.label-unavailable');
          unavailableLabels.forEach(label => {
            if (!label.classList.contains('visually-hidden')) {
              label.classList.add('visually-hidden');
            }
          });
        } catch (e) {
          console.error('Error parsing selected variant data:', e);
        }
      }
    }
  });
}
